import { useCallback, useEffect, useMemo, useState } from 'react';

import { TimelineDeleteChangeDuringDrag, TranscriptExtension } from './types';
import { Clip } from '@/player-config/src/types/clip-types';
import usePreviousEffect from '@/hooks/usePreviousEffect';
import { getAllCompositeSegments, getEndForSegment, getStartForSegment } from '@/player-config/src/utils/clipUtils';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';

import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

const DEFAULT_TRANSCRIPT_EXTENSION = {
  startTime: 0,
  startSegmentId: ''
};

export default function useTranscriptExtension({ clipData }: { clipData: Clip }) {
  const [transcriptExtension, setTranscriptExtension] = useState<TranscriptExtension>(DEFAULT_TRANSCRIPT_EXTENSION);

  const [dragExtension, setDragExtension] = useState<TranscriptExtension | null>(null);

  const compositeSegmentBoundsKey = useMemo(() => {
    const compositeSegments = getAllCompositeSegments(clipData);
    return compositeSegments
      .map(segment => `${segment.id}:${segment.currentBounds.fromFrame}-${segment.currentBounds.toFrame}`)
      .join('|');
  }, [clipData]);

  const onDeleteChangeDuringDrag = useCallback(
    ({ type, startTime, endTime, compositeSegmentId }: TimelineDeleteChangeDuringDrag) => {
      if (type === 'remove') {
        const segmentStartTime = getStartForSegment({
          clip: clipData,
          segmentId: compositeSegmentId,
          isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
        });
        const segmentEndTime = getEndForSegment({
          clip: clipData,
          segmentId: compositeSegmentId,
          isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
        });
        if (segmentStartTime >= startTime) {
          setDragExtension({
            startTime,
            startSegmentId: compositeSegmentId
          });
        } else if (segmentEndTime <= endTime) {
          setDragExtension({
            endTime,
            endSegmentId: compositeSegmentId
          });
        }
      }
      if (type === 'reset') {
        setDragExtension(null);
      }
    },
    [clipData]
  );

  useEffect(() => {
    setDragExtension(null);
    const eventListener = EventBus.on(CustomEvents.TimelineDeleteChangeDuringDrag, onDeleteChangeDuringDrag);
    return () => {
      EventBus.off(CustomEvents.TimelineDeleteChangeDuringDrag, eventListener);
    };
  }, [onDeleteChangeDuringDrag]);

  const resetTranscriptExtension = useCallback(() => {
    setTranscriptExtension(DEFAULT_TRANSCRIPT_EXTENSION);
  }, []);

  usePreviousEffect(compositeSegmentBoundsKey, resetTranscriptExtension);

  return {
    transcriptExtension: dragExtension ? dragExtension : transcriptExtension,
    setTranscriptExtension
  };
}
